import CustomButton from '@/app/customComponents/CustomButton/CustomButton';
import CustomDatePicker from '@shared/customComponents/CustomDatePicker/CustomDatePicker';
import { getImmunizations, updateImmunizationList } from '@/app/services/immunizations/Immunization';
import * as Actions from '@/app/store/actions/';
import { calculateDueOverDue, generateAgeString, isSafari } from '@/utils/utils';
import { faEdit } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, CircularProgress, FormControl, IconButton, MenuItem, Select } from '@material-ui/core';
import { ArrowBackIos } from '@material-ui/icons';
import axios from '@shared/axios-shared';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import ImmunizationListDialog from './ImmunizationFilterDialog';
import Notes from './Notes';
import { useNavigate } from 'react-router-dom';

const ImmunizationView = ({
	isLoading,
	student,
	age,
	refresh,
	setRefresh,
	setCurrentState,
	isAdmin,
	token,
	isPortedLayout,
}) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [isEditingDates, setIsEditingDates] = useState(false);
	const [modifiedImmunizations, setModifiedImmunizations] = useState([]);
	const [due, setDue] = useState([]);
	const [overDue, setOverDue] = useState([]);
	const [immunizations, setImmunizations] = useState([]);
	const [isUpdating, setIsUpdating] = useState(false);
	const [submitLoading, setSubmitLoading] = useState(false);

	useEffect(() => {
		if (student && age && !isLoading) {
			const [overDuedoses, duedoses] = calculateDueOverDue(student, age);
			setDue(duedoses);
			setOverDue(overDuedoses);
		}
	}, [isLoading, student, age]);

	const handleExempt = (immu) => {
		const temp = [...modifiedImmunizations];
		const index = temp.findIndex((item) => item.id === immu.id);
		temp[index].is_exempted = temp[index].is_exempted ? 0 : 1;
		setModifiedImmunizations(temp);
	};

	const handleImmunizationSave = () => {
		for (const immu of modifiedImmunizations) {
			if (immu.is_exempted && !immu.exempt_reason) {
				dispatch(
					Actions.showMessage({
						message: `Please select exempt reason for ${immu.immunization?.name?.split('-')[0]}`,
						variant: 'error',
					})
				);
				return;
			}
		}
		setIsUpdating(true);
		updateImmunizationList(student.id, modifiedImmunizations)
			.then(() => {
				setIsUpdating(false);
				setRefresh(refresh + 1);
				setIsEditingDates(false);
			})
			.catch((err) => {
				console.log(err);
			});
	};
	const handleImmunizationFilter = (list) => {
		setIsEditingDates(false);
		dispatch(
			Actions.openDialog({
				children: (
					<ImmunizationListDialog
						enabled={student.immunizations}
						refresh={refresh}
						rows={list}
						setRefresh={setRefresh}
						studentId={student.id}
					/>
				),
			})
		);
	};
	// TODO: Investigate what's going on here. this endpoint for getImmunizations is not being found
	useEffect(() => {
		getImmunizations().then((res) => {
			setImmunizations(res.data);
		});
	}, []);
	useEffect(() => {
		if (student?.immunizations) {
			setModifiedImmunizations(student.immunizations.filter((item) => item.is_enabled));
		}
	}, [student]);

	const handleFormSubmit = () => {
		if (isEditingDates) {
			dispatch(
				Actions.showMessage({
					message: 'Please save the immunization dates before proceeding',
					variant: 'warning',
					anchorOrigin: {
						vertical: isSafari() ? 'bottom' : 'top',
						horizontal: 'center',
					},
				})
			);
			return;
		}
		if (isAdmin) {
			setSubmitLoading(true);
			axios
				.post('/api/v1/school/parent/token/submit', { student_id: student.id })
				.then(() => {
					dispatch(
						Actions.showMessage({
							message: 'Form submitted successfully',
							variant: 'success',
						})
					);
					navigate(`/students/student${student.id}`);
				})
				.catch(() => {
					dispatch(
						Actions.showMessage({
							message: 'Something went wrong',
							variant: 'error',
						})
					);
					setSubmitLoading(false);
				});
		} else {
			setSubmitLoading(true);
			axios
				.post('/api/v1/parent/token/submit', { token, student_id: student.id })
				.then(() => {
					dispatch(
						Actions.showMessage({
							message: 'Form submitted successfully',
							variant: 'success',
						})
					);
				})
				.catch(() => {
					dispatch(
						Actions.showMessage({
							message: 'Something went wrong',
							variant: 'error',
						})
					);
				})
				.finally(() => {
					setSubmitLoading(false);
				});
		}
	};

	return (
		<>
			<div className="P-m text-pd-xxs sm:text-pd-xs md:text-pd-base">
				<div className="immunization  items-center flex-nowrap  mx-auto">
					<h4 className="md:text-pd-lg lg:text-pd-xl self-end font-extrabold">Immunizations</h4>
					<p>
						Immunizations tracks past student immunization dates and calculates overdue and due vaccines based on CDC
						recommendations.
					</p>
					<div className="flex justify-between items-start mt-10 main-box-width" style={{ gap: 10 }}>
						<div className="box-imm flex items-center flex-nowrap justify-between main-box-div">
							<h3 className="space-btm md:text-pd-lg lg:text-pd-xl self-end font-extrabold mt-10">
								CDC Recommendations
							</h3>
							<div className="recomd-imm p-32 sm:p-64 grid grid-cols-2 gap-10">
								<div className="font-bold mr-10 mb-4">
									<span className="bg-red rounded-full py-4 px-8 sm:px-10 md:px-12 text-white font-extrabold mr-4">
										!
									</span>
									{overDue?.filter((item) => item?.count !== 0)?.length} Overdue
								</div>
								<div>
									<p className="">
										{overDue?.map((item, index) => {
											if (item?.count !== 0) {
												return (
													<React.Fragment key={index}>
														{item?.name}: {item?.count}
														{item?.count === 1 ? ' dose ' : ' doses '} over due
														<br />
													</React.Fragment>
												);
											}
											return null;
										})}
										{overDue?.filter((item) => item?.count !== 0)?.length === 0 ? 'No over due doses' : ''}
									</p>
								</div>
								<div className="font-bold mr-10">
									<span className="bg-green rounded-full py-4 px-4 sm:px-6 text-white font-extrabold mr-4">
										<i className="fas fa-check" />
									</span>
									{due?.length} Due
								</div>
								<div>
									<p className="">
										{due?.map((item) => {
											return (
												<React.Fragment key={item}>
													{item}
													<br />
												</React.Fragment>
											);
										})}
										{due?.length === 0 ? 'No dose due' : ''}
									</p>
								</div>
							</div>
						</div>
						<div className="box-imm flex items-center flex-nowrap justify-between main-box-div">
							<Notes isLoading={isLoading} setRefresh={setRefresh} student={student} />
						</div>
					</div>
				</div>
			</div>
			<div className="P-m Immunizations-inner text-pd-xxs sm:text-pd-xs md:text-pd-base">
				<Box className=" mx-auto pb-10">
					<div className="w-full flex justify-between  mx-auto">
						<h4 className="md:text-pd-lg lg:text-pd-xl self-end font-extrabold">Immunizations</h4>
						<div className="flex">
							<IconButton
								className="mr-4"
								disabled={isLoading}
								onClick={() => handleImmunizationFilter(immunizations)}
								size="small"
							>
								<img alt="filter" className="fiterimg" src="assets/images/setting-icon.png" width="35px" />
							</IconButton>
							{isEditingDates ? (
								(isUpdating && <CircularProgress size={35} />) || (
									<CustomButton onClick={handleImmunizationSave} variant="primary">
										Done
									</CustomButton>
								)
							) : (
								<CustomButton
									disabled={isLoading}
									fontSize="14px"
									onClick={() => setIsEditingDates(true)}
									variant="secondary"
								>
									<div className="flex justify-center items-center">
										<FontAwesomeIcon icon={faEdit} />
										<span className="spacebtn">Edit</span>
									</div>
								</CustomButton>
							)}
						</div>
					</div>
				</Box>
				<Box className="bg-white p-12 mx-auto mb-44 overflow-auto ">
					<table className="w-full">
						<thead className="hidden sm:table-header-group">
							<tr style={{ borderBottom: '1px solid #DBDBDB' }}>
								{!isEditingDates ? <th style={{ style: '25%' }} /> : null}
								<th
									style={{
										width: '15%',
										height: '55px',
										color: '#002E6C',
										fontWeight: '700',
									}}
								>
									Dose 1
								</th>
								<th
									style={{
										width: '15%',
										height: '55px',
										color: '#002E6C',
										fontWeight: '700',
									}}
								>
									Dose 2
								</th>
								<th
									style={{
										width: '15%',
										height: '55px',
										color: '#002E6C',
										fontWeight: '700',
									}}
								>
									Dose 3
								</th>
								<th
									style={{
										width: '15%',
										height: '55px',
										color: '#002E6C',
										fontWeight: '700',
									}}
								>
									Dose 4
								</th>
								<th
									style={{
										width: '15%',
										height: '55px',
										color: '#002E6C',
										fontWeight: '700',
									}}
								>
									Dose 5
								</th>
							</tr>
						</thead>
						<tbody className="border-b">
							<tr className="age-height	" style={{ columnSpan: 6 }}>
								<td colSpan={6} style={{ color: '#06071D', fontWeight: '800' }}>
									Student age: {generateAgeString(student?.date_of_birth)}
								</td>
							</tr>
							{student?.immunizations
								?.filter((imm) => imm.is_enabled)
								.map((immu, i) => {
									return (
										<React.Fragment key={immu.id}>
											<tr className="bg-blue-200 tr-blue">
												<td className="p-8" colSpan="6" style={{ color: '#06071D', fontWeight: '800' }}>
													<div className="flex items-center justify-between">
														<div>{immu.immunization.name}</div>
														{isEditingDates ? (
															<div className="flex items-center align-center" style={{ gap: 20 }}>
																{immu.is_exempted ? (
																	<FormControl className="font-normal">
																		<Select
																			className="exempt-reason-select"
																			defaultValue="0"
																			id="reason"
																			name="reason"
																			onClick={(e) => {
																				const temp = [...modifiedImmunizations];
																				const index = temp.findIndex((item) => item.id === immu.id);
																				temp[index].exempt_reason = e.target.value;
																				setModifiedImmunizations(temp);
																			}}
																			placeholder="Exempt Reason"
																			value={immu.exempt_reason || '0'}
																		>
																			<MenuItem disabled value="0">
																				Exempt Reason
																			</MenuItem>
																			<MenuItem value="medical">Medical</MenuItem>
																			<MenuItem value="personal-believe">Personal believe</MenuItem>
																			<MenuItem value="immue">Immune</MenuItem>
																			<MenuItem value="unknown">Unknown</MenuItem>
																			<MenuItem value="other">Other</MenuItem>
																		</Select>
																	</FormControl>
																) : (
																	''
																)}
																<div
																	className="flex items-center cursor-pointer"
																	onClick={() => handleExempt(immu, i)}
																	onKeyDown={(e) => {
																		if (e.key === 'Enter') {
																			handleExempt(immu, i);
																		}
																	}}
																	role="button"
																	style={{ gap: 10 }}
																	tabIndex={0}
																>
																	<div
																		className="tick-wrapper-edit-dates"
																		style={{
																			backgroundColor: immu.is_exempted ? '#4DA0EE' : 'white',
																		}}
																	>
																		<i className="fas fa-check" />
																	</div>
																	<div className="font-bold">Exempt</div>
																</div>
															</div>
														) : null}
													</div>
												</td>
											</tr>
											{isEditingDates && !immu.is_exempted ? (
												<tr className="flex flex-col sm:table-row">
													{modifiedImmunizations[
														modifiedImmunizations?.findIndex((item) => item.id === immu.id)
													]?.meta.map((dose, ind) => {
														return dose.skip ? (
															<td key={ind}>
																<SkippedDate
																	doseIndex={ind}
																	immuIndex={i}
																	modifiedImmunizations={modifiedImmunizations}
																	setModifiedImmunizations={setModifiedImmunizations}
																/>
															</td>
														) : (
															<td key={ind}>
																<EditImmunizationDateDiv
																	doseIndex={ind}
																	immuIndex={i}
																	modifiedImmunizations={modifiedImmunizations}
																	setModifiedImmunizations={setModifiedImmunizations}
																	studentAge={student?.date_of_birth}
																/>
															</td>
														);
													})}
													{[1, 2, 3, 4, 5].slice(0, 5 - immu.meta.length).map((num) => {
														return (
															<td key={num}>
																<EditImmunizationDateDiv disabledDiv />
															</td>
														);
													})}
												</tr>
											) : (
												(isEditingDates && immu.is_exempted && (
													<tr>
														<td>
															<div className="py-28 px-6">Uncheck exempt to add dates.</div>
														</td>
													</tr>
												)) || (
													<>
														<tr className="records">
															<td className="p-8 " style={{ color: '#06071D', fontWeight: '800' }}>
																Student Records
															</td>
															{immu.is_exempted ? (
																<>
																	<td
																		className="p-8 text-center"
																		style={{
																			backgroundColor: '#FFF8C7',
																			fontWeight: '600',
																		}}
																	>
																		Exempt
																	</td>
																	<td className="p-8" colSpan={4} style={{ backgroundColor: '#FFF8C7' }} />
																</>
															) : (
																immu.meta.map((dose, index) => {
																	if (dose.is_recurring === '1') {
																		return (
																			<td
																				className="p-8 text-center"
																				key={index}
																				style={{
																					fontWeight: '600',
																				}}
																			/>
																		);
																	}
																	if (dose.skip) {
																		return (
																			<td
																				className="p-8 text-center"
																				key={index}
																				style={{
																					fontWeight: '600',
																				}}
																			>
																				Skipped
																			</td>
																		);
																	}
																	return (
																		<td
																			className="p-8 text-center"
																			key={index}
																			style={{
																				backgroundColor: dose.date
																					? 'white'
																					: age?.allMonths <= dose.max_duration
																						? '#DFFFED'
																						: '#FFEBEA',
																				fontWeight: '600',
																			}}
																		>
																			{dose.date
																				? dayjs(dose.date).format('MM/DD/YYYY')
																				: age?.allMonths <= dose.max_duration
																					? 'Due'
																					: 'Overdue'}
																		</td>
																	);
																})
															)}
														</tr>

														<tr className="records-second">
															<td className="p-8" style={{ color: '#06071D', fontWeight: '800' }}>
																CDC Recommendation
															</td>
															{immu.meta.map((dose, index) => {
																return (
																	<td
																		className="p-8 text-center"
																		key={index}
																		style={{
																			color: '#06071D',
																			fontWeight: '600',
																		}}
																	>
																		{dose.duration_text}
																	</td>
																);
															})}
														</tr>
													</>
												)
											)}
										</React.Fragment>
									);
								})}
						</tbody>
					</table>
				</Box>
				{!isPortedLayout ? (
					<div className="w-full flex justify-between gap-6 mt-20 mb-80 pb-80 lg:mb-64 lg:pb-64">
						<CustomButton
							fontSize="15px"
							height="46"
							onClick={() => setCurrentState('contacts')}
							variant="primary"
							width="120px"
						>
							<div className="flex items-center justify-center">
								<ArrowBackIos className="" style={{ fontSize: '1.6rem' }} />
								Previous
							</div>
						</CustomButton>
						{submitLoading ? (
							<CircularProgress size={35} />
						) : (
							<CustomButton fontSize="15px" height="46" onClick={handleFormSubmit} width="120px">
								Submit
							</CustomButton>
						)}
					</div>
				) : null}
			</div>
		</>
	);
};

export default ImmunizationView;

const EditImmunizationDateDiv = ({
	modifiedImmunizations,
	setModifiedImmunizations,
	immuIndex,
	doseIndex,
	disabledDiv,
	studentAge,
}) => {
	const [disabled, setDisabled] = useState(!!doseIndex);

	useEffect(() => {
		let t = false;
		for (let i = 0; i < doseIndex; i += 1) {
			if (modifiedImmunizations[immuIndex]?.meta[i].date === null && !modifiedImmunizations[immuIndex]?.meta[i].skip) {
				t = true;
				break;
			}
		}
		setDisabled(t);
	}, [modifiedImmunizations]);

	const handleSkip = () => {
		if (disabledDiv) {
			return;
		}
		if (doseIndex !== 0) {
			if (
				modifiedImmunizations[immuIndex].meta[doseIndex - 1].date === null &&
				!modifiedImmunizations[immuIndex].meta[doseIndex - 1].skip
			) {
				return;
			}
		}
		const temp = [...modifiedImmunizations];
		temp[immuIndex].meta[doseIndex].skip = 1;
		setModifiedImmunizations(temp);
	};
	const handleDateChange = (date) => {
		const temp = [...modifiedImmunizations];
		temp[immuIndex].meta[doseIndex].date = date ? dayjs(date).format('YYYY-MM-DD') : '';
		setModifiedImmunizations(temp);
	};
	return (
		<div
			className={`flex flex-row sm:flex-col items-center sm:items-start align-left p-4 sm:p-16 sm:pr-20 text-pd-xxs sm:text-pd-xs md:text-pd-base font-100 ${
				disabledDiv ? 'hidden sm:flex' : ''
			}`}
			style={{ gap: 20 }}
		>
			<div className="flex-grow sm:hidden">Dose: {doseIndex + 1}</div>
			<CustomDatePicker
				disableFuture
				disabled={disabled || disabledDiv}
				label="Date"
				minDate={
					(!disabledDiv &&
						((doseIndex === 0 && studentAge) ||
							(modifiedImmunizations[immuIndex].meta[doseIndex - 1].skip && studentAge) ||
							modifiedImmunizations[immuIndex].meta[doseIndex - 1].date)) ||
					0
				}
				setValue={handleDateChange}
				value={disabledDiv ? '' : modifiedImmunizations[immuIndex].meta[doseIndex].date}
				width="100%"
			/>
			<div
				className="flex items-center cursor-pointer gap-2 sm:gap-8 md:gap-16"
				onClick={handleSkip}
				onKeyDown={(e) => {
					if (e.key === 'Enter' || e.key === ' ') {
						handleSkip();
					}
				}}
				role="button"
				tabIndex={0}
			>
				<div className="tick-wrapper-edit-dates">
					<i className="fas fa-check" />
				</div>
				<div>Skip</div>
			</div>
		</div>
	);
};

const SkippedDate = ({ modifiedImmunizations, setModifiedImmunizations, immuIndex, doseIndex }) => {
	const handleNotSkip = () => {
		const temp = [...modifiedImmunizations];
		temp[immuIndex].meta[doseIndex].skip = 0;
		setModifiedImmunizations(temp);
	};

	return (
		<div className="flex items-center align-left py-6 sm:py-20 text-pd-xxs sm:text-pd-xs md:text-pd-base">
			<div
				className="flex items-center cursor-pointer"
				onClick={handleNotSkip}
				onKeyDown={(e) => {
					if (e.key === 'Enter' || e.key === ' ') {
						handleNotSkip();
					}
				}}
				role="button"
				style={{ gap: 10 }}
				tabIndex={0}
			>
				<div className="tick-wrapper-edit-dates" style={{ background: '#4DA0EE' }}>
					<i className="fas fa-check" />
				</div>
				<div>Skipped</div>
			</div>
		</div>
	);
};
